// src/hooks/useRealtimeConnection.js
import { useRef, useState, useCallback, useEffect } from 'react';
import API_BASE_URL from '../config/api';

export function useRealtimeConnection() {
  const pcRef = useRef(null);
  const dataChannelRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const isConnectingRef = useRef(false);

  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isDataChannelReady, setIsDataChannelReady] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({
    iceConnectionState: '',
    connectionState: '',
    dataChannelState: '',
    audioPlaying: false,
    audioLevel: 0,
    lastMessageReceived: ''
  });

  // Hilfsfunktion: Warten auf ICE-Gathering mit Timeout
  const waitForIceGatheringComplete = (pc, timeoutMs = 5000) => {
    return new Promise((resolve) => {
      if (pc.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const timeout = setTimeout(() => {
        pc.removeEventListener('icegatheringstatechange', checkState);
        resolve(); // Fortfahren auch ohne alle Kandidaten
      }, timeoutMs);

      function checkState() {
        if (pc.iceGatheringState === 'complete') {
          clearTimeout(timeout);
          pc.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }
      }
      pc.addEventListener('icegatheringstatechange', checkState);
    });
  };

  // Audio-Element-Listener einrichten
  useEffect(() => {
    const audioEl = remoteAudioRef.current;
    if (!audioEl) return;

    const handlePlaying = () => {
      setDebugInfo((prev) => ({ ...prev, audioPlaying: true }));
    };
    audioEl.addEventListener('playing', handlePlaying);

    return () => {
      if (audioEl) {
        audioEl.removeEventListener('playing', handlePlaying);
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    isConnectingRef.current = false;
    
    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    setIsConnected(false);
    setIsDataChannelReady(false);
    setIsConnecting(false);
    setError(null);
    setDebugInfo((prev) => ({ ...prev, audioPlaying: false, dataChannelState: 'closed' }));
  }, []);

  /**
   * Aufbau der Realtime-Verbindung.
   */
  const connectRealtime = useCallback(async (languageParam, vocabularyPair) => {
    // Verhindere mehrfache gleichzeitige Verbindungsversuche
    if (isConnectingRef.current) {
      return;
    }
    
    // Bestehende Verbindung trennen
    if (pcRef.current) {
      disconnect();
    }

    isConnectingRef.current = true;
    setIsConnecting(true);
    setError(null);
    setIsDataChannelReady(false);

    try {
      // Ephemeral Key vom Backend holen
      const queryParams = new URLSearchParams();
      if (languageParam) queryParams.set('language', languageParam);
      if (vocabularyPair && vocabularyPair.german && vocabularyPair.foreign) {
        queryParams.set('vocabulary', JSON.stringify(vocabularyPair));
      }

      const res = await fetch(`${API_BASE_URL}/api/realtime/ephemeral-key?${queryParams.toString()}`);
      
      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(`Failed to get ephemeral key: ${res.status} ${errorText}`);
      }
      
      const jsonData = await res.json();
      
      if (!jsonData.client_secret?.value) {
        throw new Error('Invalid response: missing client_secret');
      }
      
      const ephemeralKey = jsonData.client_secret.value;

      // RTCPeerConnection mit mehreren STUN-Servern für bessere Konnektivität
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' }
        ]
      });

      pc.ontrack = (event) => {
        if (event.track.kind === 'audio') {
          if (remoteAudioRef.current) {
            remoteAudioRef.current.srcObject = event.streams[0];
            remoteAudioRef.current
              .play()
              .catch((err) => {
                console.warn('Audio playback blocked:', err);
              });
          }
        }
      };

      pc.oniceconnectionstatechange = () => {
        setDebugInfo((prev) => ({
          ...prev,
          iceConnectionState: pc.iceConnectionState
        }));
      };

      pc.onconnectionstatechange = () => {
        setDebugInfo((prev) => ({
          ...prev,
          connectionState: pc.connectionState
        }));
        
        if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') {
          setError('Connection failed');
          disconnect();
        }
      };

      // DataChannel erstellen
      const dc = pc.createDataChannel('oai-events');
      
      dc.onopen = () => {
        dataChannelRef.current = dc;
        setIsDataChannelReady(true);
        setIsConnected(true);
        setIsConnecting(false);
        isConnectingRef.current = false;
        setDebugInfo((prev) => ({ ...prev, dataChannelState: 'open' }));
      };

      dc.onclose = () => {
        setIsDataChannelReady(false);
        setDebugInfo((prev) => ({ ...prev, dataChannelState: 'closed' }));
      };

      dc.onerror = (err) => {
        console.error('DataChannel error:', err);
        setError('DataChannel error');
      };

      dc.onmessage = async (e) => {
        try {
          const msg = JSON.parse(e.data);
          setMessages((prev) => [msg, ...prev]);
          setDebugInfo((prev) => ({
            ...prev,
            lastMessageReceived: new Date().toISOString()
          }));
        } catch (err) {
          console.error('Message handling error:', err);
        }
      };

      // Lokales Audio
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 48000,
          echoCancellation: true,
          noiseSuppression: true
        }
      });
      stream.getTracks().forEach((track) => {
        pc.addTrack(track, stream);
      });

      // Local Offer erstellen
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      
      // Auf ICE-Gathering warten (mit Timeout)
      await waitForIceGatheringComplete(pc, 5000);

      // SDP über Backend an OpenAI senden (um CORS zu umgehen)
      const sdpResponse = await fetch(`${API_BASE_URL}/api/realtime/sdp-exchange`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sdp: pc.localDescription.sdp,
          ephemeralKey: ephemeralKey
        })
      });
      
      if (!sdpResponse.ok) {
        const errorData = await sdpResponse.text();
        throw new Error(`SDP exchange failed: ${errorData}`);
      }
      
      const remoteSdp = await sdpResponse.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: remoteSdp });

      pcRef.current = pc;
      
    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message || 'Verbindung fehlgeschlagen');
      setIsConnecting(false);
      setIsConnected(false);
      setIsDataChannelReady(false);
      isConnectingRef.current = false;
      disconnect();
    }
  }, [disconnect]);

  const sendMessage = useCallback(
    (msgObj) => {
      if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
        return false;
      }
      try {
        dataChannelRef.current.send(JSON.stringify(msgObj));
        return true;
      } catch (err) {
        console.error('Send error:', err);
        return false;
      }
    },
    []
  );

  const handleUserMessage = useCallback((text) => {
    // SESSION_UPDATE vs. normale Nachricht
    if (text.startsWith('<<<SESSION_UPDATE>>>')) {
      const jsonPart = text.replace('<<<SESSION_UPDATE>>>', '');
      try {
        const parsed = JSON.parse(jsonPart);
        sendMessage(parsed);
        return;
      } catch (e) {
        console.error('SESSION_UPDATE JSON invalid:', e);
        return;
      }
    }
    
    // Normale Nachricht
    sendMessage({
      type: 'conversation.item.create',
      item: {
        role: 'user',
        content: text
      }
    });
    
    // Response anfordern
    sendMessage({
      type: 'response.create',
      response: {
        output_modalities: ['audio', 'text'],
        format: 'pcm16',
        sample_rate: 48000,
        stream_to_client: true,
        voice: {
          type: 'builtin',
          id: 'verse'
        },
        audio_format: {
          type: 'pcm16',
          sample_rate: 48000
        }
      }
    });
  }, [sendMessage]);

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    isDataChannelReady,
    isConnecting,
    error,
    debugInfo,
    messages,
    connectRealtime,
    disconnect,
    handleUserMessage,
    remoteAudioRef
  };
}
